import {
  Collapse,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  CollapseIcon,
  LeftArrowIcon,
  LessonIcon,
  LockedIcon,
  MoreIcon,
  SidebarWorkspaceIcon,
  SketchNoteLogoIcon,
} from '@sketchnote/icons';
import { MoreActionMenu } from '@sketchnote/shared-components';
import classNames from 'classnames';
import { Emoji } from 'emoji-mart';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import { API, endpoints } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import {
  CreateWorkspace,
  DeleteWorkspace,
  useWorkspace,
  WorkspaceState,
} from 'src/components/workspaces';
import { useLMSDashboardStore } from 'src/features/lms/store/useDashboardStore';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayoutState } from 'src/shared/hooks/use-layout';
import shallow from 'zustand/shallow';

import { LmsDashboardConstant } from '../../../features/lms/utils/constants';
import { useSidebarStore } from '../store';

const { useState, useEffect, useCallback } = React;

export const LmsInstructorWrapper = ({
  isOpen,
  children,
  isOverview = false,
  onToggle,
  showHome = true,
}: {
  isOpen: boolean;
  children: React.ReactNode;
  isOverview?: boolean;
  showHome?: boolean;
  onToggle?: ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void) | undefined;
}): JSX.Element => {
  const router = useRouter();
  const authData = useAuth((s) => s.userDetails);
  const memberRole = authData?.memberInformation?.role;
  const { setOnCreateWorkspace } = useWorkspace();

  const { workspaces, fetchSidebar } = useSidebarStore(
    (s) => ({
      workspaces: s.workspaces,
      fetchSidebar: s.fetchSidebar,
      toggleWorkspace: s.toggleWorkspace,
      isWorkspaceOpen: s.isWorkspaceOpen,
      toggleWorkspaceById: s.toggleWorkspaceById,
    }),
    shallow
  );
  const {
    isOpen: isSettingsMenuOpened,
    onOpen: onOpenSettingsMenu,
    onClose: onCloseSettingsMenu,
  } = useDisclosure();
  const [hoverWorkspaceId, setHoverWorkspaceId] = useState('');
  const [isMoreMenuOpened, setIsMoreMenuOpened] = useState(false);
  const [activeWorkspaceIndex, setActiveWorkspaceIndex] = useState<number>(0);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const sideBarClasses = classNames({
    'h-full inset-0': true,
    'w-256': isOverview,
    'flex-col py-14 overflow-y-auto border-r border-gray-200': true,
    hidden: !isOpen,
    flex: isOpen,
  });
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const { setIsInstructorSidebar } = useLMSDashboardStore();
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);
  const { workspaceModal, setWorkspaceModal } = useLayoutState();

  const handleWorkspaceModal = () => {
    setWorkspaceModal(true, 'create');
  };

  useEffect(() => {
    setOnCreateWorkspace(handleCreateWorkspace);
  }, []);

  const handleCreateWorkspace: WorkspaceState['onCreateWorkspace'] = useCallback(
    async ({ name, privacy, members, icon, description }) => {
      const payload = {
        name: name,
        isPublic: privacy === 'public',
        icon: icon,
        description: description,
        members: members,
        lms: true,
      };
      const response = await API.post(endpoints.workspaces.creationWorkspace.url, payload);

      if (response) {
        fetchSidebar();
        return response.data.success;
      }
    },
    []
  );

  const handleDeleteWorkspace = async () => {
    const response = await API.delete(
      `/workspace?id=${workspaces[activeWorkspaceIndex]?.workspaceId}`
    );

    if (response.data.success) {
      setIsDeleteOpen(false);
      fetchSidebar();
      Analytics.track('Workspace deleted', {
        'Deleted workspace name': workspaces[activeWorkspaceIndex]?.name,
        'Deleted workspace id': workspaces[activeWorkspaceIndex]?.workspaceId,
      });
      //TODO: Route to the next workspace on delete current workspace
      router.push(`/${mode}/${orgId}/dashboard`, '', {
        shallow: true,
      });
    }
  };

  return (
    <>
      <nav className={sideBarClasses}>
        <div className="px-14">
          <div className="flex justify-between cursor-pointer mb-16">
            <Link
              href={authData?.authentication === 'authorised' ? `/${mode}/${orgId}/dashboard` : '/'}
            >
              <a tabIndex={-1} className="flex items-center">
                <SketchNoteLogoIcon className="text-primary-500 text-lg" />
                <span className="text-primary-800 font-extraBold ml-6 text-sm">sketchnote</span>
              </a>
            </Link>
            <button tabIndex={-1} onClick={onToggle as any}>
              <CollapseIcon wrapperClass="text-gray-500" size={24} />
            </button>
          </div>

          <button
            className="mb-15 py-10 px-12 flex items-center text-primary-500"
            onClick={() => {
              setIsInstructorSidebar(false);
            }}
          >
            <LeftArrowIcon />
            <span className=" ml-13 font-sans font-medium text-sm leading-20">Back</span>
          </button>

          <section className="border-b">
            <button
              className="mb-15 py-10 px-12 flex items-center text-primary-500"
              onClick={() => {
                router.push(`/${mode}/${orgId}/lms`);
              }}
            >
              <SidebarWorkspaceIcon />
              <span className=" ml-6 font-sans font-medium text-sm leading-20">
                Instructor dashboard
              </span>
            </button>
          </section>
          <section className="text-gray-500 text-sm py-10 border-b -ml-2">
            <button
              onClick={() => {
                setIsWorkspaceOpen(!isWorkspaceOpen);
              }}
              className="w-full flex justify-between pl-17 pr-16 items-center"
            >
              <div className="flex text-gray-600">
                <span className="block pt-2 mr-4">
                  <LessonIcon />
                </span>
                &nbsp;Workspaces
              </div>
              <div
                className="block"
                style={{
                  transform: isWorkspaceOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'all 0.5s',
                }}
              >
                <ChevronDownIcon wrapperClass="mt-2 pt-4" />
              </div>
            </button>
            <Collapse in={isWorkspaceOpen}>
              <div className="mt-16 mb-5">
                {memberRole !== 'guest' && (
                  <button onClick={handleWorkspaceModal} className="text-blue-500 flex pb-10 px-12">
                    <AddIcon wrapperClass="text-xs mt-3 mr-11 " />
                    <span className="block text-sm font-medium">&nbsp;Create new workspace</span>
                  </button>
                )}
                {authData?.authentication === 'authorised' && (
                  <>
                    {workspaces.length > 0 ? (
                      workspaces?.map(
                        (
                          {
                            _id: worksapce_id,
                            name,
                            workspaceId: id,
                            files: workspacefiles,
                            open: isOpenWorkspace,
                            icon,
                            isPublic,
                            lms,
                          },
                          index
                        ) => {
                          return (
                            <section
                              key={id}
                              className={classNames({
                                ' border-gray-200 ': true,
                              })}
                            >
                              {lms && (
                                <>
                                  <div
                                    className={classNames({
                                      'flex justify-between text-gray-600 py-10 px-12 rounded-sm':
                                        true,
                                      // 'text-primary-500 bg-primary-100 hover:bg-blue-200 py-10':
                                      //   isActiveWorkspaceDashboard(id),

                                      'hover:bg-gray-100': !isOpenWorkspace,
                                    })}
                                    onMouseOver={() => {
                                      !isSettingsMenuOpened && onOpenSettingsMenu();
                                      setHoverWorkspaceId(id);
                                    }}
                                    onMouseLeave={() => {
                                      isSettingsMenuOpened && onCloseSettingsMenu();
                                      setHoverWorkspaceId('');
                                    }}
                                  >
                                    <div className="flex">
                                      <div>
                                        <Link href={`/${mode}/${orgId}/lms/w/${worksapce_id}`}>
                                          <a>
                                            <div className="flex" title={`${name}`}>
                                              <span className="flex  items-center">
                                                {icon?.type === 'image' ? (
                                                  <img
                                                    className="w-14 h-14 object-center object-cover"
                                                    src={icon?.id}
                                                  />
                                                ) : (
                                                  <Emoji
                                                    emoji={
                                                      icon?.id || {
                                                        id: 'file_folder',
                                                      }
                                                    }
                                                    set="apple"
                                                    size={14}
                                                  />
                                                )}
                                              </span>
                                              &nbsp;
                                              <span className="block text-sm  font-medium truncate w-105  text-left">
                                                {name}
                                              </span>
                                            </div>
                                          </a>
                                        </Link>
                                      </div>
                                    </div>

                                    <div>
                                      {(isSettingsMenuOpened || isMoreMenuOpened) &&
                                        hoverWorkspaceId === id && (
                                          <div className="flex">
                                            {!isPublic && (
                                              <LockedIcon className="mt-2 mr-3 text-sm" />
                                            )}
                                            <MoreActionMenu
                                              onCloseMenu={() => {
                                                setIsMoreMenuOpened(false);
                                                onCloseSettingsMenu();
                                                setHoverWorkspaceId('');
                                              }}
                                              onOpenMenu={() => {
                                                setHoverWorkspaceId(id);
                                                setIsMoreMenuOpened(true);
                                              }}
                                              menuItems={[{ label: 'Delete', value: 'delete' }]}
                                              classNames={{ menuWrap: 'text-gray-600' }}
                                              icon={<MoreIcon className="text-gray-600 mt-3" />}
                                              onClickMenuItem={(e) => {
                                                setActiveWorkspaceIndex(index);
                                                if (e.value === 'delete') {
                                                  setIsDeleteOpen(true);
                                                }
                                              }}
                                              placement="right"
                                            />
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </section>
                          );
                        }
                      )
                    ) : (
                      <div className="text-gray-600 px-12">
                        {LmsDashboardConstant?.sidebarCreateWorkspaceInfo}
                      </div>
                    )}
                  </>
                )}
              </div>
            </Collapse>
          </section>
        </div>
        {children}
      </nav>
      <Modal isOpen={workspaceModal.create} onClose={() => setWorkspaceModal(false, 'create')}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46.4rem',
            borderRadius: '0.8rem',
            margin: 'auto',
            backgroundColor: 'white',
            padding: '32px',
          }}
        >
          <ModalCloseButton className="text-xs text-gray-500  m-24" />
          <ModalBody>
            <CreateWorkspace
              onClose={() => setWorkspaceModal(false, 'create')}
              totalWorkspaces={workspaces.length || 0}
              hidePrivacy={true}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)}>
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46.4rem',
            borderRadius: '0.8rem',
            margin: 'auto',
            backgroundColor: 'white',
            padding: '32px',
          }}
        >
          <ModalCloseButton className="text-xs text-gray-500  m-24" />
          <ModalBody>
            <DeleteWorkspace
              workspaceName={workspaces[activeWorkspaceIndex]?.name}
              onClose={() => setIsDeleteOpen(false)}
              handleDeleteWorkspace={handleDeleteWorkspace}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
